import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { default as autoTable, CellHookData, TableWidthType } from 'jspdf-autotable';

import { TitleService } from '@shared/services';

export function exportToPDF(
  tableNativeElement: string | HTMLTableElement,
  titleService: TitleService,
  pageSize: string,
  tableWidth: TableWidthType
): void {
  const [doc] = parseTableData(tableNativeElement, pageSize, tableWidth);
  doc.save(`${titleService.items.map((x) => x.title).join('_')}.pdf`);
}

export function exportToExcel(tableNativeElement: string | HTMLTableElement, titleService: TitleService): void {
  const [, table] = parseTableData(tableNativeElement);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Table');

  [...table.table.head, ...table.table.body].forEach((tableRow, ri) => {
    const row = worksheet.getRow(ri + 1);
    let startCellIndex = 1;
    Object.values(tableRow.cells).forEach((tableCell) => {
      const cell = row.getCell(startCellIndex);
      const cellElement = tableCell.raw as HTMLTableCellElement;

      // Handle column span
      if (tableCell.colSpan > 1) {
        worksheet.mergeCells({
          top: ri + 1,
          bottom: ri + 1,
          left: startCellIndex,
          right: startCellIndex + tableCell.colSpan - 1,
        });
      }

      // Handle style
      cell.alignment = { horizontal: tableCell.styles.halign };

      if (cellElement?.classList) {
        const classList = Array.from(cellElement.classList);

        if (classList.includes('bg-gray-900')) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
              argb: '111827',
            },
          };
        }

        if (classList.includes('bg-blue-100')) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
              argb: 'dbeafe',
            },
          };
        }

        if (classList.includes('bg-blue-200')) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
              argb: 'bfdbfe',
            },
          };
        }

        if (classList.includes('bg-blue-300')) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
              argb: '93c5fd',
            },
          };
        }

        if (classList.includes('text-center')) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }

        if (classList.includes('text-right')) {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }

        if (classList.includes('text-white')) {
          cell.font = { ...cell.font, color: { argb: 'ffffff' } };
        }

        if (classList.includes('font-bold')) {
          cell.font = { ...cell.font, bold: true };
        }
      }

      // Handle value
      const cellValueStr = tableCell.text.join().trim();
      let cellValue: string | number = cellValueStr;
      let numFmt: string | undefined;

      if (ri > 0 && startCellIndex > 2) {
        const isPercent = cellValueStr.includes('%');

        const currencyMatch = cellValueStr.match(/USD|EUR|PLN|NOK|SEK/i);

        const numericString = cellValueStr
          .replace(/\s/g, '')
          .replace(/[^0-9.,-]/g, '')
          .replace(',', '.');

        if (numericString !== '' && !isNaN(Number(numericString))) {
          if (isPercent) {
            cellValue = Number(numericString) / 100;
            numFmt = '0.00%';
          } else if (currencyMatch) {
            cellValue = Number(numericString);
            const currencyCode = currencyMatch[0].toUpperCase();
            numFmt = `#,##0.00 [$${currencyCode}]`;
          } else {
            cellValue = Number(numericString);
            numFmt = '#,##0.00';
          }
        }
      }

      cell.value = cellValue;

      if (numFmt) {
        cell.numFmt = numFmt;
      }

      const textWidth = cellValueStr.length + 3;
      const currentColumn = worksheet.getColumn(startCellIndex);

      if (!currentColumn.width || textWidth > currentColumn.width) {
        currentColumn.width = textWidth < 80 ? textWidth : 80;
      }

      row.commit();
      startCellIndex += tableCell.colSpan;
    });
  });

  workbook.xlsx
    .writeBuffer()
    .then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `${titleService.items.map((x) => x.title).join('_')}.xlsx`);
    })
    .catch((error) => {
      console.error('Error writing Excel file:', error);
    });
}

export function parseTableData(
  tableNativeElement: string | HTMLTableElement,
  pageSize: string = 'a0',
  tableWidth: TableWidthType = 'auto'
): [jsPDF, CellHookData] {
  let dataTable = null;
  const doc = new jsPDF('l', 'pt', pageSize);

  autoTable(doc, {
    html: tableNativeElement,
    tableWidth: tableWidth,
    headStyles: {
      fillColor: [51, 189, 224],
      lineWidth: 0.2,
      lineColor: [215, 215, 215],
    },
    bodyStyles: {
      textColor: 0,
      lineWidth: 0.2,
      lineColor: [215, 215, 215],
    },
    alternateRowStyles: { fillColor: [235, 234, 232] },
    columnStyles: {
      0: { cellWidth: 150 },
      1: { cellWidth: 150 },
      2: { cellWidth: 150 },
      3: { cellWidth: 200 },
    },
    didParseCell: function (data: CellHookData) {
      const cellElement = data.cell.raw as HTMLTableCellElement;
      const cellContent = cellElement?.textContent?.trim();
      if (!isNaN(parseFloat(cellContent)) && isFinite(Number(cellContent))) {
        data.cell.styles.halign = 'right';
      } else {
        data.cell.styles.halign = 'left';
      }

      if (data.section === 'head' && data.row.index === 0) {
        data.cell.styles.fillColor = 120;
      }

      if (cellElement?.classList) {
        const classList = Array.from(cellElement.classList);
        if (classList.includes('bg-gray-900')) {
          data.cell.styles.fillColor = 0;
        }
        if (classList.includes('bg-blue-100')) {
          data.cell.styles.fillColor = [219, 234, 254];
        }
        if (classList.includes('bg-blue-200')) {
          data.cell.styles.fillColor = [191, 219, 254];
        }
        if (classList.includes('bg-blue-300')) {
          data.cell.styles.fillColor = [147, 197, 253];
        }
        if (classList.includes('text-white')) {
          data.cell.styles.textColor = 255;
        }
        if (classList.includes('font-bold')) {
          data.cell.styles.fontStyle = 'bold';
        }
        if (classList.includes('text-center')) {
          data.cell.styles.halign = 'center';
        }
        if (classList.includes('text-right')) {
          data.cell.styles.halign = 'right';
        }
      }
    },
    didDrawPage: function (data: CellHookData) {
      dataTable = data;
    },
  });

  return [doc, dataTable];
}
